import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";

interface AccountTierModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentTier: string | null;
  userName: string;
}

export const AccountTierModal = ({
  isOpen,
  onClose,
  userId,
  currentTier,
  userName,
}: AccountTierModalProps) => {
  const [selectedTier, setSelectedTier] = useState(currentTier || "standard");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const updateTierMutation = useMutation({
    mutationFn: async (newTier: string) => {
      const { data, error } = await supabase
        .from("profiles")
        .update({ admin_tier: newTier })
        .eq("id", userId)
        .select();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: "Account Tier Updated",
        description: `Successfully updated account tier for ${userName}`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      onClose();
    },
    onError: (error) => {
      console.error("Error updating account tier:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update account tier. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateTierMutation.mutate(selectedTier);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Change Account Tier</DialogTitle>
          <DialogDescription>
            Update the account tier for {userName}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <Label htmlFor="tier-selection" className="text-sm font-medium">
            Select Account Tier
          </Label>

          <RadioGroup
            value={selectedTier}
            onValueChange={setSelectedTier}
            className="grid gap-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="standard" id="standard" />
              <Label htmlFor="standard" className="text-sm">
                Standard - Regular account
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="freemium" id="freemium" />
              <Label htmlFor="freemium" className="text-sm">
                Freemium - Premium access
              </Label>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateTierMutation.isPending}>
            {updateTierMutation.isPending ? "Updating..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
